import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import MoreContentRequester from 'components/MoreContentRequester';
import { useExtensionPresence } from 'hooks/useExtensionPresence';
import { usePortalName } from 'hooks/usePortalConnections';
import PortalConnection from 'models/PortalConnection';
import { portalConnectionQueryKeys } from 'services/api/portalConnections';
import { startPortalScans } from 'services/api/portalScans';
import { workflowQueryKeys } from 'services/api/workflows';
import { useToastActions } from 'stores/toastStore';
import { Body, BodySmall } from 'styles/typography';

import ScanCard from './ScanCard';
import { orderScanCards, reportScanResults, ScanCard as ScanCardModel, scanCard } from './scanCards';
import ScanResultsSlideout from './ScanResultsSlideout';
import StartScanModal from './StartScanModal';
import { TaskSort } from './taskCards';
import useScanRunner, { outstandingDiscovery } from './useScanRunner';
import useScans from './useScans';

interface Props {
  connections: PortalConnection[];
  // The `Start a Portal Scan` button lives in the page header; the modal it opens (and the
  // start mutation) stay here with the list they change.
  startScanOpen: boolean;
  onStartScanClose: () => void;
  // Opens a task's slideout — owned by the page, which holds the one slideout both tabs use.
  onOpenTask: (workflowId: string, siblingWorkflowIds: string[]) => void;
  // The page's sort and source-system filter — their controls (the toolbar row and the
  // FilterBar) render outside this tab's tree. This receives only the applied values, plus a
  // reset for the filtered empty state.
  sort: TaskSort;
  sourceSystems: string[];
  onClearFilters: () => void;
}

/**
 * The Portal Scans tab: one card per `portal_discovery` run, newest first, plus the multi-select
 * that starts new ones.
 *
 * The page shell around this — header, tab bar, active-scan banner — belongs to TasksPage. This
 * owns the list, its results slideout, and the start gesture.
 */
export default function PortalScansTab({
  connections,
  startScanOpen,
  onStartScanClose,
  onOpenTask,
  sort,
  sourceSystems,
  onClearFilters,
}: Props) {
  const portalName = usePortalName();
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();
  const { workflows, isLoading, isError, query } = useScans(sort);
  const { runScan, runScans, active } = useScanRunner();
  const { present } = useExtensionPresence();

  const [openResults, setOpenResults] = useState<ScanCardModel | null>(null);

  const cards = useMemo(
    () =>
      orderScanCards(
        workflows.map((workflow) => scanCard(workflow, { portalName })),
        sort
      ).filter((card) => sourceSystems.length === 0 || sourceSystems.includes(card.sourceSystem)),
    [workflows, portalName, sort, sourceSystems]
  );

  // Runs whose sweep has not happened yet: created, waiting on this browser, and nobody driving
  // them. A reload mid-sweep lands here, as does a sweep that failed — the run stays open, so the
  // card offers to pick it up rather than stranding it.
  const awaitingBrowser = useMemo(
    () => new Set(workflows.filter((workflow) => outstandingDiscovery(workflow)).map((workflow) => workflow.id)),
    [workflows]
  );

  // Which portals sweep in THIS browser. Headless runs are picked up by a worker; extension and
  // manual clients have no worker, so the page drives the sweep itself or nothing does.
  const drivenHere = useMemo(
    () => new Set(connections.filter((connection) => !connection.isHeadless).map((c) => c.sourceSystem)),
    [connections]
  );

  const start = useMutation({
    mutationFn: (sourceSystems: string[]) => startPortalScans(sourceSystems),
    onSuccess: async (results) => {
      onStartScanClose();
      reportScanResults(results, portalName).forEach((text) => addToast({ text }));
      // Both lists move: the scan list gains rows, and the connection rows gain the
      // `liveScanWorkflowId` the page's banner is keyed on.
      queryClient.invalidateQueries({ queryKey: workflowQueryKeys.lists });
      queryClient.invalidateQueries({ queryKey: portalConnectionQueryKeys.list() });

      // Creating the run is not running it. In extension mode the sweep happens here, so the
      // runs this start just created are driven straight away — otherwise Start Scan would leave
      // them sitting in `action_required` while the banner claimed a scan was under way.
      const toDrive = results.filter(
        (result) => result.created && result.workflowId && drivenHere.has(result.sourceSystem)
      );
      if (toDrive.length > 0) await runScans(toDrive.map((result) => result.workflowId!));
    },
    onError: () => addToast({ text: 'We couldn’t start the scan. Try again in a moment.' }),
  });

  return (
    <Wrapper>
      {isError ? (
        <Message data-testid='scans-error'>We couldn’t load your portal scans.</Message>
      ) : isLoading ? (
        <Message data-testid='scans-loading'>Loading portal scans…</Message>
      ) : cards.length === 0 ? (
        sourceSystems.length > 0 ? (
          <Message data-testid='scans-empty-filtered'>
            <Body>No portal scans match these filters.</Body>
            <ClearLink type='button' onClick={onClearFilters}>
              Clear filters
            </ClearLink>
          </Message>
        ) : (
          <Message data-testid='scans-empty'>
            <Body>No portal scans yet.</Body>
            <BodySmall>Start one to check your portals for new referral activity.</BodySmall>
          </Message>
        )
      ) : (
        <Cards data-testid='scan-cards'>
          {cards.map((card) => (
            <ScanCard
              key={card.id}
              card={card}
              onOpenResults={setOpenResults}
              // Only offered where this browser is the thing that sweeps, and only once the
              // extension has actually answered — headless runs belong to a worker, and without
              // the extension there is nothing here to run them with.
              onRunInBrowser={
                present === true && drivenHere.has(card.sourceSystem) && awaitingBrowser.has(card.workflowId)
                  ? () => runScan(card.workflowId)
                  : undefined
              }
              runStatus={active?.workflowId === card.workflowId ? active.status : undefined}
            />
          ))}
        </Cards>
      )}
      {/* Outside the cards branch on purpose: the source-system filter can empty the LOADED
          pages while matches still sit on unloaded ones, and the sentinel staying visible is
          what keeps them coming. It renders nothing once the last page is in. */}
      {!isError && !isLoading && <MoreContentRequester resource={query} />}

      {startScanOpen && (
        <StartScanModal
          connections={connections}
          submitting={start.isPending}
          onCancel={onStartScanClose}
          onStart={(sourceSystems) => start.mutate(sourceSystems)}
        />
      )}

      <ScanResultsSlideout
        card={openResults}
        onClose={() => setOpenResults(null)}
        onOpenTask={(workflowId, siblings) => {
          setOpenResults(null);
          onOpenTask(workflowId, siblings);
        }}
      />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Message = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 48px 0;
  text-align: center;
  color: ${({ theme }) => theme.colors.black75};
`;

const ClearLink = styled.button`
  font-family: ${({ theme }) => theme.textStyles.base.fontFamily};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.blue100};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
`;
